import React, {Component, Fragment} from 'react';
import PropTypes from "prop-types";
import {UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledTooltip} from 'reactstrap';
import {observer} from "mobx-react";
//TODO: Upgrade to hooks version of Dropzone
import Dropzone from 'react-dropzone';
import classnames from 'classnames';

import {MediaAttachment} from './MediaAttachment';

import UploadProgress from "./UploadProgress";
import {imageMimeTypes, videoMimeTypes, primaryReportExtensionNames} from './extensions';
import {GALLERY, UPLOAD} from "./types";


/**
 * takes array<T> and returns array<array<T>> where the sub arrays are of max length size
 * @param list
 * @param size
 */
const grouped = (list, size) => {
  const result = [];
  list.forEach(x => {
    if (result.length === 0) {
      result.push([])
    }
    const current = result[result.length - 1];
    if (current.length < size) {
      current.push(x)
    } else {
      result.push([x])
    }
  });
  return result
};

const imageFillStyle = { width: '100%', height: '100%', objectFit: 'cover' };

@observer
class UploadGallery extends Component {

  state = {
    imageErrors: {}, // A map of image index => validation errors,
    imageUploadErrors: [] // An array of validation errors of a recently uploaded image
  };

  static propTypes = {
    uploadGalleryStore: PropTypes.object.isRequired,
    imageSelectedCallback: PropTypes.func,
    maxFileSizeBytes: PropTypes.number,
    minImageWidthPixels: PropTypes.number,
    maxImageWidthPixels: PropTypes.number,
    minImageHeightPixels: PropTypes.number,
    maxImageHeightPixels: PropTypes.number,
    imageAspectRatio: PropTypes.number,

    aspectRatioPrettyReadable: PropTypes.string // Like '16:9' for display to the user
  };

  // Returns a promise of an array with errors; empty array if no errors
  validateUploadedImage (fileToBeChecked) {
    const errors = [];

    const img = fileToBeChecked.target;

    const requiresImageSizeValidation = !!img && !!this.props.minImageWidthPixels || !!this.props.maxImageWidthPixels || !!this.props.minImageHeightPixels || !!this.props.maxImageHeightPixels || !!this.props.imageAspectRatio;

    if (!requiresImageSizeValidation) {
      return new Promise(resolve => resolve([]));
    }

    const props = this.props;

    return new Promise(resolve => {
      const reader = new FileReader;
      reader.onload = function () {
        const img = new Image;
        img.onload = function () {
          if ((!!props.maxImageHeightPixels && img.naturalHeight > props.maxImageHeightPixels) || (!!props.maxImageWidthPixels && img.naturalWidth > props.maxImageWidthPixels)) {
            errors.push(`Image is too large.  Must be no larger than ${props.maxImageWidthPixels} x ${props.maxImageHeightPixels}`);
          }
          if ((!!props.minImageHeightPixels && img.naturalHeight < props.minImageHeightPixels) || (!!props.minImageWidthPixels && img.naturalWidth < props.minImageWidthPixels)) {
            errors.push(`Image is too small.  Must be no smaller than ${props.minImageWidthPixels} x ${props.minImageHeightPixelsPixels}`);
          }
          if ((!!props.imageAspectRatio && (Math.abs(props.imageAspectRatio - (img.naturalWidth / img.naturalHeight))) > .1))  {
            errors.push(`Wrong aspect ratio. You need ${props.aspectRatioPrettyReadable}`);
          }
          resolve(errors);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(fileToBeChecked);
    });
  }

  constructor(props) {
    super(props);
    this.uploadGalleryStore = this.props.uploadGalleryStore;
    if (this.props.imageSelectedCallback) {
      this.uploadGalleryStore.setImageSelectedCallback(this.props.imageSelectedCallback);
    }

    const setState = this.setState.bind(this);

    this.uploadGalleryStore.setImageUploadedErrorCallback((error) => setState({
      imageUploadErrors: [error]
    }));
  }

  renderUploadGallery(imageErrors) {
    const errors = this.renderErrorMessage();
    const uploadProgress = this.renderUploadProgress();
    const navTabs = this.renderNavTabs();
    const galleryTabContent = this.renderGalleryTabContent(imageErrors);
    const uploadTabContent = this.renderUploadTabContent();

    return (
      <div className="upload-gallery" >
        {errors}
        {navTabs}
        {uploadProgress}
        {galleryTabContent}
        {uploadTabContent}
      </div>
    );
  }

  renderErrorMessage() {
    return this.state.imageUploadErrors.length > 0 ? <div className="alert alert-danger">An error occurred during the upload
      <br />
      {this.state.imageUploadErrors.map(error => <div>{error}</div>)}
   </div> : undefined;
  }

  renderUploadProgress() {
    const {uploadActive, uploadProgress, dropRejected} = this.uploadGalleryStore;
    if (!uploadActive) return undefined;

    if (dropRejected) {
      return (
        <div>
          <div className="alert alert-danger alert-dismissable">
            File must be less than 15MB, an image or video, and one the following extensions:
            {
              primaryReportExtensionNames.map((x, i) =>
                <span key={x}><code>{x}</code>{i === primaryReportExtensionNames.length - 1 ? '' : ', '}</span>
              )
            }
            <button type="button" className="close" onClick={() => this.uploadGalleryStore.toggleDropRejected()}>×</button>
          </div>
        </div>
      );
    } else {
      return (
        <div>
          <label>Upload progress</label>
          <UploadProgress percentComplete={uploadProgress * 100} />
          {
            uploadProgress === 1 ? <span><span className="fas fa-spinner fa-spin"/> Finalizing...</span> : undefined
          }
        </div>
      );
    }
  }

  renderNavTabs() {
    const {activeTab} = this.uploadGalleryStore;
    return (
      <div>
        <ul className="nav nav-pills">
          <li className="nav-item">
            <a onClick={() => this.uploadGalleryStore.setActiveTab(GALLERY)} className={`nav-link ${activeTab === 'gallery' ? 'active' : ''}`}>Gallery</a>
          </li>
          <li className="nav-item">
            <a onClick={() => this.uploadGalleryStore.setActiveTab(UPLOAD)} className={`nav-link ${activeTab === 'upload' ? 'active' : ''}`}>Upload New Media</a>
          </li>
        </ul>
      </div>
    );
  }

  checkDimensions (mediaIndex) {
    return function (event) {
      const img = event.target;
      const errors = [];

      const requiresImageSizeValidation = !!img && !!this.props.minImageWidthPixels || !!this.props.maxImageWidthPixels || !!this.props.minImageHeightPixels || !!this.props.maxImageHeightPixels;

      if (!requiresImageSizeValidation) {
        return [];
      }

      if ((!!this.props.maxImageHeightPixels && img.naturalHeight > this.props.maxImageHeightPixels) || (!!this.props.maxImageWidthPixels && img.naturalWidth > this.props.maxImageWidthPixels)) {
        errors.push(`Image is too large.  Must be no larger than ${this.props.maxImageWidthPixels} x ${this.props.maxImageHeightPixels}`);
      }
      if ((!!this.props.minImageHeightPixels && img.naturalHeight < this.props.minImageHeightPixels) || (!!this.props.minImageWidthPixels && img.naturalWidth < this.props.minImageWidthPixels)) {
        errors.push(`Image is too small.  Must be no smaller than ${this.props.minImageWidthPixels} x ${this.props.minImageHeightPixels}`);
      }
      if ((!!this.props.imageAspectRatio && (Math.abs(this.props.imageAspectRatio - (img.naturalWidth / img.naturalHeight))) > .1))  {
        errors.push(`Wrong aspect ratio. You need ${this.props.aspectRatioPrettyReadable}`);
      }

      // Only set state if there are errors, there is a big performance hit to the render method here
      if (errors.length > 0) {
        this.setState((state) => ({
          ...state,
          imageErrors: {...state.imageErrors, [mediaIndex]: errors}
        }));
      }

      return errors;
    }
  }

  renderGalleryTabContent(imageErrors) {
    const {activeTab, galleryFiles, searchText, selectedFile, uploadOffset} = this.uploadGalleryStore;

    if (galleryFiles && galleryFiles.length === 0) {
      return (
        <div className={'tab-content' + (activeTab === 'gallery' ? '' : ' d-none')} style={{
          minHeight: "400px"
        }}>
          <h5 className="text-muted text-center" style={{paddingTop: '50px'}}>No Media Found</h5>
        </div>
      );
    }

    const rowSize = 3;

    // We have galleryFiles.
    return (
      <div className={'tab-content' + (activeTab === 'gallery' ? '' : ' d-none')} style={{
        minHeight: "400px",
        height: "95%",
        maxHeight: "95%",
        overflowY: 'auto',
        overflowX: 'hidden'
      }}>
        <div style={{marginTop: '20px'}} className="container-fluid selector-gallery">
          <div>
            <div className="input-group" style={{width: '200px', marginLeft: "auto"}}>
              <input type="text" className="form-control" placeholder="Filter results" onChange={(e) => this.uploadGalleryStore.setSearchText(e.target.value) }/>
              <span className="input-group-append"><i className="input-group-text d-flex align-items-center far fa-search"></i></span>
            </div>
            { galleryFiles.length ? <h5 className="card-spacer-top card-spacer-bottom">Recently Uploaded:</h5> : '' }
          </div>
          {
            grouped(galleryFiles.filter(x => {
              const verify = (text) => text.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;
              return (!!x.name && verify(x.name)) || (!!x.url && verify(x.url))
            }), rowSize).map((row, rowIndex) => {
              return (
                <div className="row" key={rowIndex}>
                  {
                    row.map((media, mediaIndex) => {
                      // We may want to avoid media.contentType.indexOf('video')
                      const realMediaIndex = mediaIndex + rowSize * rowIndex;
                      return (
                        <Fragment>
                        <div className="col-sm-4 px-1" key={media._id ? media._id.$oid : 'new-upload'} id={"galleryAttachment-" + realMediaIndex}>
                          <div className={classnames({"card border rounded mb-2 image-card": true, selected: media === selectedFile })} >
                            <div className='card-header'>
                              <div className="d-flex align-items-center">
                                <div className="item-caption w-100 text-truncate">{media.sourceFile}</div>
                                <UncontrolledDropdown direction="left" className="mr-auto">
                                  <DropdownToggle className="btn dropdown-toggle" className=""><span className="ss-icon ss-ellipsisvertical"></span></DropdownToggle>
                                  <DropdownMenu flip={false}>
                                    <DropdownItem onClick={() => this.uploadGalleryStore.deleteChatterUpload(media)}>
                                      <i className="fas fa-trash-alt" /> Delete
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </div>
                            </div>
                            <div className='card-body card-footerless d-flex align-items-center clickable hover' style={{height: '180px', cursor: (imageErrors[realMediaIndex] && imageErrors[realMediaIndex].length > 0 ? "initial" : "pointer"), overflow: 'hidden', opacity: (imageErrors[realMediaIndex] && imageErrors[realMediaIndex].length > 0 ? ".65" : "1") }}>
                              <MediaAttachment
                                key={media.url}
                                src={media.thumbnailUrl || media.url}
                                imageStyle={imageFillStyle}
                                onLoad={this.checkDimensions(realMediaIndex).bind(this)}
                                onClick={(metadata) => {
                                  if (!Array.isArray(imageErrors[realMediaIndex])) {
                                    this.onSelectedFile(media, metadata)
                                  }
                                }}/>
                            </div>
                          </div>
                        </div>
                          {imageErrors[realMediaIndex] && imageErrors[realMediaIndex].length > 0 && <UncontrolledTooltip placement="bottom" target={"galleryAttachment-" + realMediaIndex}>
                            {imageErrors[realMediaIndex].join(", ")}
                          </UncontrolledTooltip>}
                        </Fragment>
                      );
                    })
                  }
                </div>
              )
            })
          }
          <div className="row text-center my-2">
            <button type="button" className="btn btn-block btn-secondary" onClick={() => this.uploadGalleryStore.getExistingImages(uploadOffset)}>Load More</button>
            {/* we need to update waypoint so we can use scrollable scrollableAncestor <Waypoint onEnter={() => this.uploadGalleryStore.getExistingImages(uploadOffset)} bottomOffset={-100} scrollableAncestor={document.querySelector(".modal")}/>*/}
          </div>
        </div>
      </div>
    );
  }

  onSelectedFile = (file, metadata) => {
    this.uploadGalleryStore.setSelectedFile({...file, ...metadata});
  };

  renderUploadTabContent() {
    //Separate into image, video and have props for each.
    let iAccept= [];
    if (this.props.fileTypes){
      const types = this.props.fileTypes.split(",").map(x => x.trim())
      if(types.includes("video")){
        iAccept = iAccept.concat(videoMimeTypes)
      }
      if(types.includes("images")){
        iAccept = iAccept.concat(imageMimeTypes)
      }
    }

    const {activeTab} = this.uploadGalleryStore;

    const requiresImageSizeValidation = !!this.props.minImageWidthPixels || !!this.props.maxImageWidthPixels || !!this.props.minImageHeightPixels || !!this.props.maxImageHeightPixels;

    return (
      <div className={'tab-content card-spacer-top' + (activeTab === 'upload' ? '' : ' d-none')} style={{minHeight: "400px"}}>
        <Dropzone
            style={{width: '100%', border: '1px dashed #DDD', borderRadius: '3px', cursor: 'pointer'}}
            maxSize={this.props.maxFileSizeBytes}
            accept={iAccept.join(', ')}
            onDropRejected={() => this.uploadGalleryStore.toggleDropRejected()}
            onDropAccepted={this.handleDropAccepted.bind(this)}
          >
          <span style={{display: 'block', verticalAlign: 'middle'}}>
            <div className="top-text content-spacer-top" style={{"margin-bottom": "15px"}}>
              Drop or click to upload an image or video<br /><br />

              Save images in .jpg, .gif, or .png format only. There is a 2 MB limit on all image uploads, but we recommend using image files of less than 500 KB for best results.
              {requiresImageSizeValidation && <div>
                Additionally, images must be:
                <ul>
                  {this.props.maxImageWidthPixels && <li>Larger than {this.props.minImageWidthPixels}px x {this.props.minImageHeightPixels}px</li>}
                  {this.props.minImageWidthPixels && <li>Smaller than {this.props.maxImageWidthPixels}px x {this.props.maxImageHeightPixels}px</li>}
                  {this.props.aspectRatioPrettyReadable && <li> Aspect Ratio {this.props.aspectRatioPrettyReadable}</li>}
                </ul>
              </div>}
            </div>
          </span>
        </Dropzone>
      </div>
    );
  }

  handleDropAccepted = (file) => {
    const setState = this.setState.bind(this);
    // Because we specify acceptable MIME types, this should only be an image.
    this.validateUploadedImage(file[0])
      .then(errors => {
        console.warn(errors);
        if (errors.length === 0) {
          this.uploadGalleryStore.saveChatterUpload(file[0]);
        }
        setState({
          imageUploadErrors: errors
        })
      });
  };

  render() {
    const {imageErrors} = this.state;

    return this.renderUploadGallery(imageErrors);
  }
}

UploadGallery.defaultProps = {
  maxFileSizeBytes: 1024 * 1024 * 80 // Max file size in bytes
};

export default UploadGallery;
